import { FC, ForwardedRef } from 'react';
import lodashGet from 'lodash/get';
import Checkbox from '../checkbox';
import TextInput from '../text-input';
import { FormikValues } from 'formik';
import DateInput from '../date-input';
import { FormProps } from '../helpers';
import DateRangeInput from '../date-range';
import Switch from '../switch';
import OnOffToggleButton from '../on-off-toggle-button';
import Select from '../select';
import { FormHandle } from '../../../hooks/use-form-handle.hook';
import { ObjectSchema, AnyObject } from 'yup';
import SelectMulti from '../select-multi';
import ListToggle from '../list-toggle';
import ListEditor, { ListItem } from '../list-editor';
import { DateRangeValue, UiOption } from '@/lib/helpers';
import SwitchList from '../switch-list';
import OnOffList from '../on-off-list';
import PublicServiceAcl from '../public-service-acl';
import GroupsListToggle from '../groups-list-toggle';
import PolicyPublicActionSelect from '../policy-public-action-select';
import AwsRegionSelect from '../aws-region-select';
import DatadogSiteSelect from '../datadog-site-select';
import SdEntityList from '../sd-entity-list';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import SdActionSelect from '../sd-action-select';
import DatasourcesListToggle from '../datasources-list-toggle';
import ProxyAuthTypeSelect from '../proxy-auth-type-select';
import ProxyIpRangeListEditor from '../proxy-ip-range-list-editor';
import { PublicServiceConfig as PublicServiceConfigV1 } from '@/lib/models/policy-model-v1/public-services';
import { SensitiveDataEntity } from '@/lib/models/policy-model-v1/sensitive-data';
import { McpControls as McpControlsV1 } from '@/lib/models/policy-model-v1/mcp-controls';
import { McpEntity } from '@/lib/models/policy-model/mcp-controls';
import TelemetryTypeSelect from '../telemetry-type-select';
import McpToolsAcl from '../mcp-tools-acl';
import McpToolActionSwitch from '../mcp-tool-action-switch';
import { McpAction } from '@/lib/models/policy-model-v1';
import NewServicesList from '../new-services-list';
import PublicServiceAclV1 from '../policy-v1/public-service-acl-v1';
import NewServicesListV1 from '../policy-v1/new-services-list-v1';
import McpToolsAclV1 from '../policy-v1/mcp-tools-acl-v1';
import McpToolActionSwitchV1 from '../policy-v1/mcp-tool-action-switch-v1';
import PolicyPublicActionSelectV1 from '../policy-v1/policy-public-action-select-v1';
import SdEntityListV1 from '../policy-v1/sd-entity-list-v1';
import SdActionSelectV1 from '../policy-v1/sd-action-select-v1';
import { fieldIsRequired, getErrorText, InputControlType, InputControlValue } from './helpers';
import type { OnOffEntity } from '../on-off-list/helpers';
import VertexRegionSelect from '../vertex-region-select';
import AgentsListToggle from '../agents-list-toggle';
import { PublicServiceConfig } from '@/lib/models/policy-model/public-services';
import { ProxyIpRangeConfig } from '@/lib/models/connector/proxy-connector.model';
import BooleanSelect from '../boolean-select';
import RegexPatternInput from '../regex-pattern-input';
import SelectSearch from '@/components/select-search';

// @todo ts won't catch if you do something silly like pass props like { type: 'text', options: [...]}. in other words, we need some way to validate props against the known type
interface Props extends FormProps {
  type?: InputControlType;
  formHandle: FormHandle<FormikValues>;
  autoComplete?: string;
  onEnter?: (value: string) => void;
  rows?: number;
  options?: UiOption[];
  multiple?: boolean;
  leftTitle?: string;
  rightTitle?: string;
  height?: string | number;
  inputRef?: ForwardedRef<HTMLInputElement>;
  onChange?: (val: InputControlValue) => void;
  labelChecks?: boolean;
  maxLength?: number;
  textPrefix?: string;
  updateOnly?: boolean;
  length?: number;
  hint?: string;
  copyClip?: boolean;
  inline?: boolean;
  minDate?: string | Date;
  maxDate?: string | Date;
  policy?: PolicyModel | PolicyModelV1;
  orgPolicy?: PolicyModel | PolicyModelV1;
  defaultPolicy?: PolicyModel;
  aclVariant?: 'allow' | 'block';
  idField?: string;
  withGovCloud?: boolean;
  loading?: boolean;
}

const InputControl: FC<Props> = ({
  formHandle,
  name,
  label,
  disabled = false,
  fullWidth = true,
  type = 'text',
  autoComplete = '',
  autoFocus = false,
  onEnter = () => null,
  rows,
  options = [],
  leftTitle = '',
  rightTitle = '',
  height,
  size = 'medium',
  inputRef,
  onChange,
  readonly = false,
  labelChecks,
  loadState,
  maxLength,
  textPrefix = '',
  updateOnly,
  length = 16,
  hint = '',
  emptyMessage = '',
  copyClip = false,
  inline = true,
  minDate = '',
  maxDate = '',
  policy,
  orgPolicy,
  defaultPolicy,
  aclVariant,
  idField,
  required: propRequired = false,
  withNotSet,
  withGovCloud = false,
  loading = false,
}) => {
  const {
    values,
    errors,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    validationSchema,
    validateOnChange,
  } = formHandle;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const value = lodashGet(values, name);
  const error = getErrorText(lodashGet(errors, name));
  const required =
    propRequired || fieldIsRequired(name, validationSchema as ObjectSchema<AnyObject>);

  const handleFormChange = (val: InputControlValue) => {
    setFieldValue(name, val, validateOnChange);

    // https://github.com/jaredpalmer/formik/issues/2059
    setTimeout(() => {
      setFieldTouched(name, validateOnChange);
    });

    onChange?.(val);
  };

  const formProps: FormProps = {
    name,
    label,
    disabled,
    autoFocus,
    error,
    errors,
    fullWidth,
    required,
    ref: inputRef,
    readonly,
    withNotSet,
  };

  switch (type) {
    case 'text':
    case 'email':
    case 'url':
    case 'number':
    case 'password':
    case 'textarea':
    default: {
      let inputType = 'text';
      if (['password', 'textarea', 'url', 'number'].includes(type)) {
        inputType = type;
      }

      return (
        <TextInput
          {...formProps}
          onChange={handleFormChange}
          onEnter={onEnter}
          value={value as string}
          type={inputType}
          autoComplete={autoComplete}
          rows={rows}
          maxLength={maxLength}
          textPrefix={textPrefix}
          updateOnly={updateOnly}
          length={length}
          hint={hint}
          copyClip={copyClip}
        />
      );
    }
    case 'regex':
      return (
        <RegexPatternInput
          {...formProps}
          label={label || 'Regex Pattern'}
          setFieldError={setFieldError}
          onChange={handleFormChange}
          onEnter={onEnter}
          value={value as string}
          autoComplete={autoComplete}
          rows={rows}
          maxLength={maxLength}
          updateOnly={updateOnly}
          length={length}
          copyClip={copyClip}
        />
      );
    case 'checkbox': {
      let checked = value as boolean;

      // formik passes an array for some reason
      if (Array.isArray(value)) {
        checked = Boolean(value.length) && value[0] === 'on';
      }

      return (
        <Checkbox
          {...formProps}
          checked={checked}
          onChange={handleFormChange}
          labelChecks={labelChecks}
        />
      );
    }
    case 'switch': {
      let checked = value as boolean;

      // formik passes an array for some reason
      if (Array.isArray(value)) {
        checked = Boolean(value.length) && value[0] === 'on';
      }

      return (
        <Switch
          {...formProps}
          size={size}
          checked={checked}
          onChange={handleFormChange}
          inline={inline}
        />
      );
    }
    case 'on-off-toggle-button': {
      return (
        <OnOffToggleButton
          {...formProps}
          value={value as undefined | boolean}
          onChange={handleFormChange}
          inline={inline}
        />
      );
    }
    case 'switch-list':
      return (
        <SwitchList
          {...formProps}
          value={value as string[]}
          options={options}
          onChange={handleFormChange}
          height={height}
          emptyMessage={emptyMessage}
        />
      );
    case 'on-off-list':
      return (
        <OnOffList
          {...formProps}
          value={value as OnOffEntity[] | undefined}
          options={options}
          idField={idField ?? 'id'}
          onChange={handleFormChange}
          height={height}
          emptyMessage={emptyMessage}
        />
      );
    case 'date':
      return (
        <DateInput
          {...formProps}
          minDate={minDate}
          maxDate={maxDate}
          value={value as Date}
          onChange={handleFormChange}
        />
      );
    case 'date-range':
      return (
        <DateRangeInput
          {...formProps}
          minDate={minDate}
          maxDate={maxDate}
          value={value as DateRangeValue}
          onChange={handleFormChange}
        />
      );
    case 'select':
      return (
        <Select
          {...formProps}
          value={value as string}
          options={options}
          onChange={handleFormChange}
          size={size}
          fullWidth
          loading={loading}
        />
      );

    case 'select-multi': {
      const handleSelectMultiChange = (options: UiOption[]) => {
        handleFormChange(options.map(({ value }) => value));
      };

      return (
        <SelectMulti
          {...formProps}
          value={value as string[]}
          options={options}
          onChange={handleSelectMultiChange}
        />
      );
    }

    case 'boolean-select':
      return (
        <BooleanSelect
          {...formProps}
          value={value as boolean | undefined}
          options={options}
          onChange={handleFormChange}
          size={size}
        />
      );

    case 'select-search':
      return (
        <SelectSearch
          {...formProps}
          value={value as string}
          options={options}
          onChange={handleFormChange}
          size={size}
          fullWidth
          loading={loading}
        />
      );

    case 'list-toggle': {
      const handleListToggleChange = (options: UiOption[]) => {
        handleFormChange(options.map(({ value }) => value));
      };

      return (
        <ListToggle
          {...formProps}
          value={value as string[]}
          options={options}
          onChange={handleListToggleChange}
          leftTitle={leftTitle}
          rightTitle={rightTitle}
          height={height}
          labelChecks={labelChecks}
          loadState={loadState}
        />
      );
    }

    case 'groups-list-toggle': {
      const handleListToggleChange = (options: UiOption[]) => {
        handleFormChange(options.map(({ value }) => value));
      };

      return (
        <GroupsListToggle
          {...formProps}
          value={value as string[]}
          onChange={handleListToggleChange}
          options={options}
        />
      );
    }

    case 'public-service-acl': {
      return (
        <PublicServiceAcl
          {...formProps}
          value={value as PublicServiceConfig}
          onChange={handleFormChange}
          aclVariant={aclVariant}
        />
      );
    }

    case 'new-services-list':
      return (
        <NewServicesList
          {...formProps}
          value={value as PublicServiceConfig}
          onChange={handleFormChange}
          policy={policy as PolicyModel}
        />
      );

    case 'mcp-tools-acl': {
      return (
        <McpToolsAcl
          {...formProps}
          value={value as McpEntity[]}
          onChange={handleFormChange}
          aclVariant={aclVariant}
        />
      );
    }

    case 'mcp-tool-action-switch': {
      return (
        <McpToolActionSwitch
          {...formProps}
          value={value as McpAction}
          onChange={handleFormChange}
        />
      );
    }

    case 'list-editor': {
      const safeValue = value ? (value as string[]) : [];

      const getOptions = (value: string[]) =>
        value.map((val: string) => ({
          label: val,
          value: val,
        }));

      const handleRemove = (_item: ListItem, index: number) => {
        const updatedValue = [...safeValue];
        updatedValue.splice(index, 1);
        handleFormChange(updatedValue);
      };
      const handleChange = (item: ListItem, editIndex: number) => {
        const updatedValue = [...safeValue];
        if (editIndex === -1) {
          updatedValue.unshift(item.value as string);
        } else {
          updatedValue[editIndex] = item.value as string;
        }

        handleFormChange(updatedValue);
      };

      return (
        <ListEditor
          items={getOptions(safeValue)}
          {...formProps}
          onRemove={handleRemove}
          onChange={handleChange}
          height={height}
          textPrefix={textPrefix}
        />
      );
    }

    case 'policy-public-action-select': {
      return (
        <PolicyPublicActionSelect
          {...formProps}
          size={size}
          value={value as PublicServiceConfig}
          onChange={handleFormChange}
        />
      );
    }

    case 'aws-region-select': {
      return (
        <AwsRegionSelect
          {...formProps}
          value={value as string}
          onChange={handleFormChange}
          withGovCloud={withGovCloud}
        />
      );
    }

    case 'datadog-site-select': {
      return (
        <DatadogSiteSelect {...formProps} value={value as string} onChange={handleFormChange} />
      );
    }

    case 'vertex-region-select': {
      return (
        <VertexRegionSelect {...formProps} value={value as string} onChange={handleFormChange} />
      );
    }

    case 'sd-entity-list':
      return (
        <SdEntityList
          {...formProps}
          value={value as SensitiveDataEntity[] | undefined}
          onChange={handleFormChange}
          policy={policy as PolicyModel}
        />
      );

    case 'sd-action-select': {
      return (
        <SdActionSelect
          {...formProps}
          value={value as string}
          onChange={handleFormChange}
          defaultPolicy={defaultPolicy}
        />
      );
    }

    case 'datasources-list-toggle': {
      const handleListToggleChange = (options: UiOption[]) => {
        handleFormChange(options.map(({ value }) => value));
      };

      return (
        <DatasourcesListToggle
          {...formProps}
          value={value as string[]}
          onChange={handleListToggleChange}
        />
      );
    }

    case 'agents-list-toggle': {
      const handleListToggleChange = (options: UiOption[]) => {
        handleFormChange(options.map(({ value }) => value));
      };

      return (
        <AgentsListToggle
          {...formProps}
          value={value as string[]}
          onChange={handleListToggleChange}
        />
      );
    }

    case 'proxy-auth-type-select': {
      return (
        <ProxyAuthTypeSelect {...formProps} value={value as string} onChange={handleFormChange} />
      );
    }

    case 'proxy-ip-range-list-editor': {
      return (
        <ProxyIpRangeListEditor
          {...formProps}
          value={value as ProxyIpRangeConfig[] | undefined}
          onChange={handleFormChange}
        />
      );
    }

    case 'public-service-acl-v1': {
      return (
        <PublicServiceAclV1
          {...formProps}
          value={value as PublicServiceConfigV1}
          onChange={handleFormChange}
          orgPolicy={orgPolicy as PolicyModelV1}
          aclVariant={aclVariant}
        />
      );
    }

    case 'new-services-list-v1': {
      return (
        <NewServicesListV1
          {...formProps}
          value={value as PublicServiceConfigV1}
          onChange={handleFormChange}
          policy={policy as PolicyModelV1}
        />
      );
    }

    case 'mcp-tools-acl-v1': {
      return (
        <McpToolsAclV1
          {...formProps}
          value={value as McpControlsV1}
          onChange={handleFormChange}
          orgPolicy={orgPolicy as PolicyModelV1}
          aclVariant={aclVariant}
        />
      );
    }

    case 'mcp-tool-action-switch-v1': {
      return (
        <McpToolActionSwitchV1
          {...formProps}
          value={value as McpAction}
          onChange={handleFormChange}
        />
      );
    }

    case 'policy-public-action-select-v1': {
      return (
        <PolicyPublicActionSelectV1
          {...formProps}
          value={value as PublicServiceConfigV1}
          onChange={handleFormChange}
          policy={policy as PolicyModelV1}
          orgPolicy={orgPolicy as PolicyModelV1}
        />
      );
    }

    case 'sd-entity-list-v1': {
      return (
        <SdEntityListV1
          {...formProps}
          value={value as SensitiveDataEntity[]}
          onChange={handleFormChange}
          policy={policy as PolicyModelV1}
          orgPolicy={orgPolicy as PolicyModelV1}
        />
      );
    }

    case 'sd-action-select-v1': {
      return (
        <SdActionSelectV1
          {...formProps}
          value={value as string}
          onChange={handleFormChange}
          policy={policy as PolicyModelV1}
          orgPolicy={orgPolicy as PolicyModelV1}
        />
      );
    }

    case 'telemetry-type-select': {
      return (
        <TelemetryTypeSelect {...formProps} value={value as string} onChange={handleFormChange} />
      );
    }
  }
};

export default InputControl;
